/*
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2024, Zumper
 */

import { Button, type ButtonProps } from '@zcode-ui/controls'
import { getDetailHref } from '@zcode-ui/web-zumper-global-state'
import {
  APP_HOSTNAME,
  addTrackingToUrl,
  keepBetaParam,
  locationDescriptorToString,
} from '@zcode/nr-utils'
import type { Dispatch } from '@zcode/state-type'
import { addServerSelectParam } from '@zcode/utils'
import { routesConstants } from '@zcode/zumper-routes'
import type { History } from 'history'
import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Link, type LinkProps, useHistory } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { getShouldRedirectToZumper } from '../helpers/getShouldRedirectToZumper'
import { isWarmLoadableUrl } from '../helpers/isWarmLoadableUrl'

type BaseLinkOrButtonProps = {
  'aria-hidden'?: boolean
  children: React.ReactNode
  className?: string
  forceButton?: boolean
  onClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  rel?: string
  role?: React.AriaRole
  tabIndex?: number
  target?: string
} & (
  | (Required<Pick<ButtonProps, 'variant'>> & Pick<ButtonProps, 'isLoading' | 'size'>)
  | {
      isLoading?: never
      size?: never
      variant?: never
    }
)

type EntityOrHrefOrTo =
  | { href?: never; to?: never; toEntity: Parameters<typeof getDetailHref>[0] }
  | { href: string; to?: never; toEntity?: never }
  | { href?: never; to: LinkProps['to']; toEntity?: never }

export type LinkOrButtonProps = BaseLinkOrButtonProps & EntityOrHrefOrTo

function isAuthURL(props: LinkOrButtonProps, history: History) {
  if (props.toEntity) {
    return false
  }
  const href = props.href ?? locationDescriptorToString(props.to, history)
  const url = new URL(href, `https://${APP_HOSTNAME}`)
  return (
    url.pathname.startsWith('/zumper-account/') &&
    (url.hostname.endsWith('.zumper.com') || url.hostname.endsWith('.zumperrentals.com'))
  )
}

export const LinkOrButton = ({ forceButton, ...props }: LinkOrButtonProps) => {
  const dispatch = useDispatch<Dispatch>()
  const history = useHistory()
  if (typeof props.to === 'string' && props.to.match(/^https?:\/\//)) {
    const { to, ...rest } = props
    props = { ...rest, href: to }
  }
  const { shouldRedirectToZumper, transformedOrigin } = props.toEntity
    ? getShouldRedirectToZumper(props.toEntity)
    : getShouldRedirectToZumper(props.href ?? locationDescriptorToString(props.to, history))
  if (forceButton || shouldRedirectToZumper) {
    return <ButtonLink {...props} transformedOrigin={transformedOrigin} />
  }
  if (isAuthURL(props, history)) {
    return <ButtonLink {...props} />
  }
  if (props.toEntity) {
    const { toEntity, ...rest } = props
    const href = getDetailHref(toEntity)
    if (
      (href.startsWith(`/${routesConstants.BLDG_BASE_URL}`) ||
        href.startsWith(`/${routesConstants.ADDR_BASE_URL}`) ||
        href.startsWith(`/${routesConstants.APTS_BASE_URL}`)) &&
      !rest.target
    ) {
      // if it's a warm-loadable URL and we're not `target`ing a new window, use a react-router-dom link
      return rest.variant ? (
        <Button as={Link} {...rest} to={keepBetaParam(href)} />
      ) : (
        <Link {...rest} to={keepBetaParam(href)} />
      )
    }
    return rest.variant ? (
      <Button as="a" {...rest} href={dispatch(addTrackingToUrl(href))} />
    ) : (
      <a {...rest} href={dispatch(addTrackingToUrl(href))} />
    )
  }
  if (props.to) {
    return props.variant ? (
      <Button as={Link} {...props} to={dispatch(addTrackingToUrl(props.to))} />
    ) : (
      <Link {...props} to={dispatch(addTrackingToUrl(props.to))} />
    )
  }
  return props.variant ? (
    <Button as="a" {...props} href={dispatch(addTrackingToUrl(props.href))} />
  ) : (
    <a {...props} href={dispatch(addTrackingToUrl(props.href))} />
  )
}

type ButtonLinkProps = LinkOrButtonProps & { transformedOrigin?: string }

const ButtonLink = ({
  className,
  href,
  onClick,
  rel,
  target,
  to,
  toEntity,
  transformedOrigin,
  ...rest
}: ButtonLinkProps) => {
  const dispatch = useDispatch<Dispatch>()
  const history = useHistory()
  const Component = rest.variant ? Button : 'button'
  return (
    <Component
      {...rest}
      className={twMerge(
        // We have global styling on the page like
        // a {
        //   color: var(--chakra-colors-gray-950);
        //   font-size: var(--chakra-fontSizes-md);
        // }
        // so if no variant was specified, we want to apply those
        // rules to this "button pretending to be a link" too:
        !rest.variant && 'text-base text-gray-950',
        className
      )}
      onClick={useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(e)
          if (!e.defaultPrevented) {
            const hrefString = toEntity
              ? getDetailHref(toEntity)
              : (href ?? locationDescriptorToString(to, history))
            if (hrefString) {
              const url = new URL(dispatch(addTrackingToUrl(hrefString)), window.location.origin)
              if (transformedOrigin && url.origin === window.location.origin) {
                const transformedOriginUrl = new URL(transformedOrigin)
                url.protocol = transformedOriginUrl.protocol
                url.host = transformedOriginUrl.host
              }
              addServerSelectParam(url)
              if (target) {
                window.open(url, target)
              } else if (
                !href &&
                isWarmLoadableUrl(removePrefix(url.href, window.location.origin), history.location)
              ) {
                history.push(removePrefix(url.href, window.location.origin))
              } else {
                window.location.assign(url.href)
              }
            }
          }
        },
        [dispatch, history, href, onClick, target, to, toEntity, transformedOrigin]
      )}
      type="button"
    />
  )
}

function removePrefix(target: string, prefix: string) {
  if (target.startsWith(prefix)) {
    return target.substring(prefix.length)
  }
  return target
}
